import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient } from "@/lib/query-client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Login from "./pages/Login";
import InviteAccept from "./pages/InviteAccept";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DataExclusion from "./pages/DataExclusion";
import Patients from "./pages/Patients";
import Calendar from "./pages/Calendar";
import NewPatientWizard from "./pages/patients/NewPatientWizard";
import RedirectNewPet from "./pages/patients/RedirectNewPet";
import ViewPet from "./pages/pets/ViewPet";
import EditPet from "./pages/pets/EditPet";
import CreateMedicalRecord from "./pages/medical-records/CreateMedicalRecord";
import ViewMedicalRecord from "./pages/medical-records/ViewMedicalRecord";
import ViewMedicalRecordReadOnly from "./pages/medical-records/ViewMedicalRecordReadOnly";
import VerifyExport from "./pages/medical-records/VerifyExport";
import PrescriptionPdfRedirect from "./pages/medical-records/PrescriptionPdfRedirect";
import TermPdfRedirect from "./pages/medical-records/TermPdfRedirect";
import VerifyPrescription from "./pages/medical-records/VerifyPrescription";
import VerifyVaccination from "./pages/applications/VerifyVaccination";
import SignTerm from "./pages/terms/SignTerm";
import StayPhotos from "./pages/daycare-hotel/share/StayPhotos";
import NotFound from "./pages/NotFound";
import { ModernAppLayout } from "./components/layouts/ModernAppLayout";
import ViewOwnerProfile from "./pages/patients/owners/ViewOwnerProfile";
import Catalog from "./pages/erp/Catalog";
import CatalogCommissionsPage from "./pages/erp/catalog/CatalogCommissionsPage";
import Sales from "./pages/erp/Sales";
import Taxes from "./pages/erp/Taxes";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";
import { ModuleGuard } from "@/components/ModuleGuard";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import Flowboard from "./pages/Flowboard";
import EditOwner from "./pages/owners/EditOwner";
import Reports from "./pages/Reports";
import { CampaignNewPage } from "./pages/reports/CampaignNew";
import { CampaignDetailPage } from "./pages/reports/CampaignDetail";
import WhatsAppAnalytics from "./pages/settings/WhatsAppAnalytics";
import WhatsAppMetaCallback from "./pages/settings/WhatsAppMetaCallback";
import HospitalizationDashboard from "./pages/hospitalization/HospitalizationDashboard";
import HospitalizationRecord from "./pages/hospitalization/HospitalizationRecord";
import HospitalizationSettings from "./pages/settings/hospitalization/HospitalizationSettings";
import DaycareHotelPage from "./pages/daycare-hotel/DaycareHotelPage";
import FinancialPage from "./pages/financial/FinancialPage";
import TeamPage from "./pages/team/TeamPage";
import TeamMemberPage from "./pages/team/member/TeamMemberPage";
import PetVetResearchForm from "./pages/pet-vet/PetVetResearchForm";
import PetVetDemoForm from "./pages/pet-vet/PetVetDemoForm";

// Lazy load backoffice admin panel
const AdminPanel = lazy(() => import("./pages/backoffice/AdminPanel"));

// Import SuperAdminGuard directly (not lazy) since it's a named export
import { SuperAdminGuard } from "@/components/backoffice/SuperAdminGuard";

// Loading fallback for lazy-loaded routes
function LazyLoadFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-4 p-8">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  );
}

const queryClient = createQueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <AuthProvider queryClient={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={
              <Navigate to="/login" replace />
            } />

            <Route path="/login" element={<Login />} />
            {/* <Route path="/signup" element={<Signup />} /> */}
            <Route path="/invite" element={<InviteAccept />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/data-exclusion" element={<DataExclusion />} />
            <Route path="/verificar" element={<VerifyExport />} />
            <Route path="/prescricao/:id/:code.pdf" element={<PrescriptionPdfRedirect />} />
            <Route path="/termo/:id/:code.pdf" element={<TermPdfRedirect />} />
            <Route path="/validar-receita" element={<VerifyPrescription />} />
            <Route path="/validar-vacina" element={<VerifyVaccination />} />
            <Route path="/assinar/:token" element={<SignTerm />} />
            <Route path="/fotos/:token" element={<StayPhotos />} />
            <Route path="/pet-vet/pesquisa" element={<PetVetResearchForm />} />
            <Route path="/pet-vet/demonstracao" element={<PetVetDemoForm />} />
            {/* Backward-compatible aliases (old /customers routes) */}
            <Route path="/home" element={<Navigate to="/calendar" replace />} />
            <Route
              path="/patients"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Patients />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/new"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <NewPatientWizard />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/owners/:id"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ViewOwnerProfile />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/owners/:id/:ownerTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ViewOwnerProfile />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/owners/:id/edit"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <EditOwner />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/pets/new"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <RedirectNewPet />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/pets/:id"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ViewPet />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/pets/:id/:petTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ViewPet />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/pets/:id/edit"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <EditPet />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/pets/:id/medical-records/new"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <CreateMedicalRecord />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/pets/:id/medical-records/new/:recordTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <CreateMedicalRecord />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medical-records/:id"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ViewMedicalRecordReadOnly />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medical-records/:id/:recordTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ViewMedicalRecordReadOnly />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medical-records/:id/edit"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ViewMedicalRecord />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medical-records/:id/edit/:recordTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ViewMedicalRecord />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Calendar />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar/flowboard"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="flowboard">
                      <Flowboard />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalog"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="core">
                      <Catalog />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalog/commissions"
              element={<ProtectedRoute><ModernAppLayout><ModuleGuard required="core"><CatalogCommissionsPage /></ModuleGuard></ModernAppLayout></ProtectedRoute>}
            />
            <Route
              path="/products-services"
              element={<Navigate to="/catalog" replace />}
            />


            <Route
              path="/whatsapp/analytics"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <WhatsAppAnalytics />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="financial">
                      <Sales />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fiscal-settings"
              element={<Navigate to="/taxes/settings" replace />}
            />
            <Route
              path="/taxes"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="fiscal">
                      <Taxes />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/taxes/:taxesTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="fiscal">
                      <Taxes />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/:salesTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="financial">
                      <Sales />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/grooming"
              element={
                <ProtectedRoute>
                  <Navigate to="/calendar/flowboard" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hospitalization"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="hospitalization">
                      <HospitalizationDashboard />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/financial/*"
              element={<ProtectedRoute><ModernAppLayout><ModuleGuard required="financial_ledger"><FinancialPage /></ModuleGuard></ModernAppLayout></ProtectedRoute>}
            />
            <Route
              path="/team"
              element={<ProtectedRoute><ModernAppLayout><TeamPage tab="people" /></ModernAppLayout></ProtectedRoute>}
            />
            <Route
              path="/team/commissions"
              element={<ProtectedRoute><ModernAppLayout><TeamPage tab="commissions" /></ModernAppLayout></ProtectedRoute>}
            />
            <Route
              path="/team/:userId/:tab?"
              element={<ProtectedRoute><ModernAppLayout><TeamMemberPage /></ModernAppLayout></ProtectedRoute>}
            />
            <Route
              path="/daycare-hotel/*"
              element={<ProtectedRoute><ModernAppLayout><ModuleGuard required={["day_care", "hotel"]}><DaycareHotelPage /></ModuleGuard></ModernAppLayout></ProtectedRoute>}
            />
            <Route
              path="/hospitalization/:id/:tab?"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="hospitalization">
                      <HospitalizationRecord />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/hospitalization"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <ModuleGuard required="hospitalization">
                      <HospitalizationSettings />
                    </ModuleGuard>
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Reports />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:reportTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Reports />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/campaigns/:campaignId"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <CampaignDetailPage />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/campaigns/new"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <CampaignNewPage />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/applications/:applicationTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Reports />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Settings />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/:settingsTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Settings />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/print/:printTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Settings />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/prescription-templates/:templateTab"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Settings />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/whatsapp-meta/callback"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <WhatsAppMetaCallback />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ModernAppLayout>
                    <Profile />
                  </ModernAppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/backoffice/admin"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyLoadFallback />}>
                    <SuperAdminGuard>
                      <ModernAppLayout>
                        <AdminPanel />
                      </ModernAppLayout>
                    </SuperAdminGuard>
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
